import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Menu, MenuItem, IconButton, Box, Container, Typography,
  Select, List, ListItem, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Checkbox
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Filter, History } from 'lucide-react';

import Task from '../utils/TaskMaster';
import TaskSelectionDialog from '../components/TaskSelectionDialog';
import TaskItem from '../components/TaskItem';
import { LoadingIndicator } from '../components/assets';
import { CornerLeftUpIcon,CornerDownRightIcon } from '../components/PixelIcons';
import CategoryPicker from '../components/CategoryPicker';
import RunningTaskCards, { runningTaskTiming } from '../components/RunningTaskCards';
import { PointsEarnedPopup } from '../components/PointCounter';
import confetti from 'canvas-confetti';
import { useClientExtensions } from '../contexts/ClientExtensionContext';

import {
  fetchAllTasks, getAllAssignedTasks, getUserStats, uploadTasks, assignTask, getFriends,
  getCategories, processRewardJob, resetAssignedTaskOrder, updateAssignedTask,
  updateTaskCategory, updateTaskPoints,
} from '../utils/api';
import { logPerf, measureAsync } from '../utils/performance';

const COMPLETION_SOUND_MUTED_STORAGE_KEY = 'dopatize:completion-sound-muted';

const getTodayUtcRange = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return {
    after: start.toISOString(),
    before: end.toISOString(),
  };
};

export const formatPriorityForDisplay = (rawPriority) => (
  `Priority: ${Task.formatUserViewPriority(rawPriority)}`
);

export const filterTaskTreeByCategory = (task, categoryFilter) => {
  if (!categoryFilter) return task;
  const matches = categoryFilter === 'uncategorized'
    ? task.category_id === null || task.category_id === undefined
    : String(task.category_id) === categoryFilter;
  const matchingChildren = (task.subtasks || [])
    .map((child) => filterTaskTreeByCategory(child, categoryFilter))
    .filter(Boolean);
  if (!matches && !matchingChildren.length) return null;
  return { ...task, subtasks: matchingChildren };
};

export const fetchAllRunningAssignedTasks = async (getPage = getAllAssignedTasks) => {
  const running = [];
  let page = 1;
  while (true) {
    const response = await getPage(page, 100, {
      start: 'notnull',
      complete: 'null',
      order_by: 'start',
    });
    running.push(...(response.tasks || []));
    if (!response.pagination?.has_next) return running;
    page += 1;
  }
};

const UserHome = () => {
  /* ---------------- state ---------------- */
  const [tasks, setTasks] = useState([]);
  const [level, setLevel] = useState(1);
  const [unassignedTasks, setUnassignedTasks] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);

  const [userStats, setUserStats] = useState(null);
  const [completedToday, setCompletedToday] = useState([]);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignError, setReassignError] = useState(null);
  const [isReassigning, setIsReassigning] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareFriends, setShareFriends] = useState([]);
  const [selectedFriendIds, setSelectedFriendIds] = useState([]);
  const [shareError, setShareError] = useState(null);
  const [shareMessage, setShareMessage] = useState(null);
  const [isLoadingFriends, setIsLoadingFriends] = useState(false);
  const [runningTasks, setRunningTasks] = useState([]);
  const [runningTaskActions, setRunningTaskActions] = useState({});
  const [runningTaskError, setRunningTaskError] = useState(null);
  const [runningNow, setRunningNow] = useState(Date.now());
  const [completionPopup, setCompletionPopup] = useState({
    open: false,
    breakdown: [],
    streakMultiplier: 0,
    totalPoints: 0,
  });
  const [pendingCompletion, setPendingCompletion] = useState(null);
  const completionAudioRef = useRef(null);
  const runningActionIdsRef = useRef(new Set());

  const navigate = useNavigate();
  const { emitTaskEvents } = useClientExtensions();

  /* ------------ helpers (memoised) ------------ */

  const sortTasksRecursively = useCallback((tree) => (
    tree
      .map((t) => ({
        ...t,
        subtasks: sortTasksRecursively(t.subtasks || []),
      }))
      .sort(
        (a, b) => (
          (a.assigned_order || Infinity) - (b.assigned_order || Infinity)
          || a.order - b.order
        ),
      )
  ), []);

  const getMaxDepth = (tree) => (
    !tree?.length ? 0 : 1 + Math.max(...tree.map((t) => getMaxDepth(t.subtasks)))
  );

  /* ---------------- data fetch ---------------- */

  const fetchTasks = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setIsLoadingTasks(true);
    try {
      const completedTodayRange = getTodayUtcRange();
      const [assignedResponse, unassignedResponse, completedResponse, stats, categoryResponse, running] = await Promise.all([
        fetchAllTasks({
          assignment: 'assigned',
          exclude: { completed: completedTodayRange },
        }),
        fetchAllTasks('not_assigned'),
        getAllAssignedTasks(1, 100, { completed: 'today' }),
        getUserStats(),
        getCategories(),
        fetchAllRunningAssignedTasks(),
      ]);
      const assignedTasks = assignedResponse.tasks || [];
      const unassigned = unassignedResponse.tasks || [];
      const completedTodayData = completedResponse.tasks || [];

      const transformStartedAt = performance.now();

      setUserStats(stats);
      setCategories(categoryResponse.categories || []);
      setCompletedToday(completedTodayData);
      setRunningTasks(running);
      setTasks(assignedTasks.map((t) => new Task(t)));
      setUnassignedTasks(unassigned);
      logPerf('frontend.user_home.transform_tasks', performance.now() - transformStartedAt, {
        totalTasks: assignedTasks.length + unassigned.length,
        completedTodayCount: completedTodayData.length,
      });
    } catch (err) {
      // eslint-disable-next-line no-alert
      if (!quiet) alert('Failed to load tasks - please try again.');
      console.error(err);
    } finally {
      setIsLoadingTasks(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    const refresh = () => fetchTasks({ quiet: true });
    window.addEventListener('assistantUpdate', refresh);
    return () => window.removeEventListener('assistantUpdate', refresh);
  }, [fetchTasks]);

  const refreshRunningTasks = useCallback(async () => {
    try {
      setRunningTasks(await fetchAllRunningAssignedTasks());
    } catch (_) {
      setRunningTaskError('Unable to refresh running tasks.');
    }
  }, []);

  useEffect(() => {
    if (!runningTasks.length) return undefined;
    const timer = window.setInterval(() => setRunningNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [runningTasks.length]);

  useEffect(() => {
    window.addEventListener('focus', refreshRunningTasks);
    return () => window.removeEventListener('focus', refreshRunningTasks);
  }, [refreshRunningTasks]);

  useEffect(() => {
    try {
      const completionSounds = require.context('../assets', false, /complete_sound.*\.mp3$/);
      const sounds = completionSounds.keys()
        .map((path) => {
          const audio = new Audio(completionSounds(path));
          audio.preload = 'auto';
          return audio;
        });
      completionAudioRef.current = sounds;
    } catch (_) {
      completionAudioRef.current = [];
    }
  }, []);

  /* ---------------- ui handlers ---------------- */

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await measureAsync('frontend.user_home.upload_tasks', () => uploadTasks(file));    // backend saves & re-calculates stats
      await measureAsync('frontend.user_home.refresh_after_upload', () => fetchTasks());         // reload once for a single source of truth
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('Failed to upload - please try again.');
      console.error(err);
    }
  };

  const handleUnassign = async () => {
    if (!selectedTask) return;

    let found = null;
    tasks.forEach((root) => {
      const t = root.getTask(selectedTask);
      if (t) found = t;
    });

    if (!found) return;  // nothing to do

    try {
      await assignTask(selectedTask, { assigned: false });
      await fetchTasks();
    } catch (err) {
      console.error(err);
    } finally {
      handleMenuClose();
    }
  };

  const handleEditClick = () => {
    if (selectedTask) navigate(`/task/${selectedTask}`);
    handleMenuClose();
  };

  const handleResetOrder = async () => {
    if (!selectedTask) return;
    try {
      await resetAssignedTaskOrder(selectedTask);
      await fetchTasks();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.data?.error || err.message || 'Failed to reset order - please try again.');
      console.error(err);
    } finally {
      handleMenuClose();
    }
  };

  const handleTaskClick = (taskId) => {
    navigate('/task-view', { state: { taskId } });
  };

  const beginRunningTaskAction = (taskId, action) => {
    if (runningActionIdsRef.current.has(taskId)) return false;
    runningActionIdsRef.current.add(taskId);
    setRunningTaskActions((current) => ({ ...current, [taskId]: action }));
    setRunningTaskError(null);
    return true;
  };

  const finishRunningTaskAction = (taskId) => {
    runningActionIdsRef.current.delete(taskId);
    setRunningTaskActions((current) => {
      const next = { ...current };
      delete next[taskId];
      return next;
    });
  };

  const emitRunningTaskEvents = (task, eventTypes) => {
    emitTaskEvents(task.task_id, eventTypes, { assignedTaskId: task.id });
  };

  const handlePauseRunningTask = async (task, timing = runningTaskTiming(task)) => {
    if (!beginRunningTaskAction(task.id, 'pausing')) return;
    try {
      await updateAssignedTask(task.id, {
        start: null,
        working_time: timing.workingTime,
      });
      setRunningTasks((current) => current.filter((item) => item.id !== task.id));
      emitRunningTaskEvents(task, ['timer_stopped']);
      await refreshRunningTasks();
    } catch (error) {
      setRunningTaskError(error.data?.error || error.message || 'Unable to pause this task.');
    } finally {
      finishRunningTaskAction(task.id);
    }
  };

  const pointsPopupData = (scoring) => {
    const scoreBreakdown = { ...scoring };
    const totalPoints = scoreBreakdown.points;
    const streakMultiplier = scoreBreakdown.streak;
    delete scoreBreakdown.points;
    delete scoreBreakdown.streak;
    delete scoreBreakdown.type;
    return {
      breakdown: Object.entries(scoreBreakdown).map(([label, points]) => ({ label, points })),
      streakMultiplier,
      totalPoints,
    };
  };

  const playCompletionFeedback = () => {
    confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
    try {
      if (window.localStorage.getItem(COMPLETION_SOUND_MUTED_STORAGE_KEY) === 'true') return;
    } catch (_) {}
    const sounds = completionAudioRef.current;
    if (!sounds?.length) return;
    const sound = sounds[Math.floor(Math.random() * sounds.length)];
    sound.currentTime = 0;
    sound.play().catch(() => {});
  };

  const handleCompleteRunningTask = async (task, timing = runningTaskTiming(task)) => {
    if (!beginRunningTaskAction(task.id, 'completing')) return;
    const completedAt = new Date();
    const completedTask = {
      ...task,
      due_date: task.due_date ? new Date(task.due_date).toISOString() : null,
      start: null,
      working_time: timing.workingTime,
      complete: completedAt.toISOString(),
    };
    try {
      await updateAssignedTask(task.id, {
        start: null,
        working_time: timing.workingTime,
        complete: completedTask.complete,
      });
    } catch (error) {
      setRunningTaskError(error.data?.error || error.message || 'Unable to complete this task.');
      finishRunningTaskAction(task.id);
      return;
    }

    setRunningTasks((current) => current.filter((item) => item.id !== task.id));
    refreshRunningTasks();
    try {
      const result = await updateTaskPoints(task.id, completedTask, 'standard');
      playCompletionFeedback();
      window.dispatchEvent(new CustomEvent('profileUpdate'));
      window.dispatchEvent(new CustomEvent('rewardsUpdate', { detail: { quiet: true } }));
      setPendingCompletion({
        task,
        rewardJobPending: Boolean(result?.reward_job_pending),
      });
      setCompletionPopup({
        open: true,
        ...pointsPopupData(result.scoring),
      });
    } catch (error) {
      setRunningTaskError('Task completed, but points could not be awarded.');
      emitRunningTaskEvents(task, ['timer_stopped', 'completed']);
      fetchTasks();
    } finally {
      finishRunningTaskAction(task.id);
    }
  };

  const handleCompletionPopupClose = () => {
    const completed = pendingCompletion;
    setCompletionPopup((current) => ({ ...current, open: false }));
    setPendingCompletion(null);
    if (!completed) return;
    emitRunningTaskEvents(completed.task, ['timer_stopped', 'completed']);
    if (completed.rewardJobPending) {
      processRewardJob()
        .then(() => window.dispatchEvent(new CustomEvent('rewardsUpdate', { detail: { quiet: true } })))
        .catch((error) => console.error('Failed to start reward generation:', error));
    }
    fetchTasks();
  };

  const handleMenuOpen = (event, taskId) => {
    setAnchorEl(event);
    setSelectedTask(taskId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTask(null);
  };

  const handleShareClick = async () => {
    if (!selectedTask) return;
    setAnchorEl(null);
    setShowShareModal(true);
    setShareError(null);
    setShareMessage(null);
    setSelectedFriendIds([]);
    setIsLoadingFriends(true);
    try {
      const data = await getFriends();
      setShareFriends(data.friends || []);
    } catch (err) {
      console.error(err);
      setShareError(err.data?.message || err.message || 'Failed to load friends.');
    } finally {
      setIsLoadingFriends(false);
    }
  };

  const selectedTaskNode = () => {
    for (const root of tasks) {
      const found = root.getTask(selectedTask);
      if (found) return found;
    }
    return null;
  };

  const handleCategoryClick = () => {
    setAnchorEl(null);
    setShowCategoryDialog(true);
  };

  const handleCategoryChange = async (categoryId) => {
    if (!selectedTask) return;
    try {
      await updateTaskCategory(selectedTask, categoryId);
      setShowCategoryDialog(false);
      setSelectedTask(null);
      await fetchTasks();
    } catch (err) {
      alert(err.data?.message || err.message || 'Unable to update category.');
    }
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setSelectedFriendIds([]);
    setShareError(null);
    setShareMessage(null);
    setSelectedTask(null);
  };

  const toggleSelectedFriend = (friendId) => {
    setSelectedFriendIds((current) => (
      current.includes(friendId)
        ? current.filter((id) => id !== friendId)
        : [...current, friendId]
    ));
  };

  const handleShareTask = async () => {
    if (!selectedFriendIds.length) {
      setShareError('Pick at least one friend.');
      return;
    }
    setShareError(null);
    setShareMessage(null);
    try {
      await assignTask(selectedTask, { assigned: true, user_ids: selectedFriendIds });
      setShareMessage('Task shared.');
      await fetchTasks();
    } catch (err) {
      console.error(err);
      setShareError(err.data?.message || err.message || 'Share failed.');
    }
  };



  /* ---------------- filters / derived data ---------------- */

  const filteredSorted = sortTasksRecursively(
    tasks.map((task) => filterTaskTreeByCategory(task, categoryFilter)).filter(Boolean),
  );

  /* ---------------- re-assign modal ---------------- */

  const handleReassignTask = async (taskId) => {
    if (!taskId) {
      setReassignError('Pick a task first'); return;
    }
    setIsReassigning(true);
    setReassignError(null);
    try {
      await assignTask(taskId);
      await fetchTasks();
      closeReassignModal();
    } catch (err) {
      console.error(err);
      setReassignError('Reassign failed – try again');
      setIsReassigning(false);
    }
  };

  const closeReassignModal = () => {
    setShowReassignModal(false);
    setReassignError(null);
    setIsReassigning(false);
  };

  /* ---------------- render helpers ---------------- */

  const formatDate = (date) => {
    if (!date) return 'No due date';
    return new Date(date).toLocaleDateString();
  };

  const formatTime = (seconds) => {
    if (!seconds) return '00:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const renderTasks = (tree, currentLevel = 1) => {
    // collect all tasks that should be rendered
    const tasksToRender = [];

    const collectTasks = (nodes, lvl) => {
      nodes?.forEach((task) => {
        const hasSubtasks = (task.subtasks?.length ?? 0) > 0;
        if (hasSubtasks && lvl < level) {
          collectTasks(task.subtasks, lvl + 1);
        } else if (!hasSubtasks || lvl === level) {
          tasksToRender.push(task);
        }
      });
    };

    collectTasks(tree, currentLevel);

    // sort by assigned_order (null/undefined = Infinity)
    const sorted = tasksToRender.sort(
      (a, b) => (a.assigned_order ?? Infinity) - (b.assigned_order ?? Infinity)
    );

    // build components in sorted order
    return sorted.map((task) => {
      const typographyList = [
        task.shared_from_username ? `From Friend: ${task.shared_from_username}` : null,
        `Completed: ${task.level}`,
        {
          text: formatPriorityForDisplay(task.priority),
          userPriority: Task.toUserViewPriority(task.priority),
        },
        `Due: ${formatDate(task.due_date)}`,
        `Estimated time: ${formatTime(task.duration)}`,
        `🔥 ${task.streak}`,
      ].filter(Boolean);



      return (
        <TaskItem
          id={`home-task-${task.id}`}
          menuButtonId={`home-task-menu-${task.id}`}
          key={task.id}
          title={task.title}
          typographyList={typographyList}
          duration={task.duration}
          workingTime={task.working_time}
          onMenuOpen={(anchor) => handleMenuOpen(anchor, task.id)}
          onClick={() => handleTaskClick(task.id)}
          type="assigned"
        />
      );
    });
  };

  /* ---------------- JSX ---------------- */

  return (
    <Container id="user-home-page" maxWidth="lg">

      {userStats && (
        <Box id="home-completed-count" sx={{ mb: 3 }}>
          <Typography variant="body1">
            Tasks Completed:&nbsp;
            {userStats.completed_tasks}
          </Typography>
        </Box>
      )}

      {runningTaskError && (
        <Alert severity="error" onClose={() => setRunningTaskError(null)} sx={{ mb: 2 }}>
          {runningTaskError}
        </Alert>
      )}

      <RunningTaskCards
        tasks={runningTasks}
        now={runningNow}
        actions={runningTaskActions}
        onOpen={(task) => handleTaskClick(task.task_id)}
        onPause={handlePauseRunningTask}
        onComplete={handleCompleteRunningTask}
      />

      {/* task tree */}
      <Box id="home-todo-section" sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          To Do
        </Typography>

        {/* toolbar */}
        <Box id="home-toolbar" sx={{ mb: 1.5 }}>
          <Box id="home-category-filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Select
            id="home-category-filter-select"
            value={categoryFilter}
            size="small"
            aria-label="Category filter"
            displayEmpty
            startAdornment={<Filter style={{ width: '1.4em', height: '1.4em', marginRight: 8, flexShrink: 0 }} />}
            onChange={(e) => setCategoryFilter(e.target.value)}
            sx={(theme) => ({
              ...theme.typography.caption,
              width: { xs: theme.spacing(22), sm: theme.spacing(25) },
              height: theme.spacing(4.5),
              m: 0,
              borderRadius: theme.spacing(1.375),
              bgcolor: 'background.default',
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
                borderColor: 'primary.main',
              },
            })}
          >
            <MenuItem value="">All categories</MenuItem>
            <MenuItem value="uncategorized">No category</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={String(category.id)}>
                {category.color && (
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: category.color, mr: 1 }} />
                )}
                {category.name}
              </MenuItem>
            ))}
          </Select>
          <Button
            id="home-reassign-button"
            aria-label="Reassign previous tasks"
            color="secondary"
            size="small"
            variant="outlined"
            startIcon={<History style={{ width: '1.2em', height: '1.2em' }} />}
            onClick={() => setShowReassignModal(true)}
            sx={(theme) => ({
              minHeight: theme.spacing(4.25),
              px: 1.25,
              m: 0,
              border: '2px solid',
              borderColor: 'secondary.main',
              borderRadius: theme.spacing(1.375),
              color: 'secondary.main',
              bgcolor: 'background.default',
              boxShadow: `0 ${theme.spacing(0.375)} 0 ${theme.palette.secondary.dark}`,
              '&:hover': {
                border: '2px solid',
                borderColor: 'secondary.main',
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                boxShadow: `0 ${theme.spacing(0.5)} 0 ${theme.palette.secondary.dark}`,
                transform: 'translateY(-1px)',
              },
              '&:active': {
                boxShadow: `0 ${theme.spacing(0.125)} 0 ${theme.palette.secondary.dark}`,
                transform: 'translateY(2px)',
              },
            })}
          >
            Reassign
          </Button>
          </Box>
        </Box>

        <Box id="home-task-level-controls" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <IconButton
            id="home-task-level-deeper"
            aria-label="Show deeper task levels"
            color="secondary"
            size="small"
            sx={{ width: 34, height: 32, border: '2px solid' }}
            onClick={() => setLevel((l) => Math.min(l + 1, getMaxDepth(tasks)))}
          >
            <CornerDownRightIcon />
          </IconButton>
          <IconButton
            id="home-task-level-shallower"
            aria-label="Show fewer task levels"
            color="secondary"
            size="small"
            sx={{ width: 34, height: 32, border: '2px solid' }}
            onClick={() => setLevel((l) => Math.max(1, l - 1))}
          >
            <CornerLeftUpIcon />
          </IconButton>
        </Box>
        {isLoadingTasks ? (
          <LoadingIndicator text="getting tasks" />
        ) : filteredSorted.length
          ? <List id="home-todo-list" sx={{display:'flex', flexDirection:'column',width:"100%", maxHeight:400, overflow:"auto", paddingLeft:"8px", paddingRight:"8px", borderRadius:8,  border:"2px solid",borderColor:"secondary.main",  scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // Internet Explorer and Edge
    '&::-webkit-scrollbar': {
      display: 'none', // Chrome, Safari, Opera
    },}} >{renderTasks(filteredSorted)}</List>
          : <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>No tasks pending.</Typography>}
      </Box>

      {/* completed today */}
      <Box id="home-completed-today" sx={{ mb: 3 ,display:'flex', flexDirection:'column', gap:2,alignItems:'center'}}>
        <Typography variant="h4" gutterBottom>
          Completed Today
        </Typography>
        {isLoadingTasks ? (
          <LoadingIndicator text="loading completed tasks" />
        ) : completedToday.length ? (
          <List id="home-completed-list" sx={{display:'flex', flexDirection:'column',width:"100%", maxHeight:400, overflow:"auto", paddingLeft:"8px", paddingRight:"8px", borderRadius:8,  border:"2px solid",borderColor:"secondary.main",    scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // Internet Explorer and Edge
    '&::-webkit-scrollbar': {
      display: 'none', // Chrome, Safari, Opera
    },}} >
            {completedToday.map((task) => {
              const typographyList = [
                `completed ${task.level + 1}`,
                `estimated time ${formatTime(task.duration)}`,
              ];
              return (
                <TaskItem
                  id={`home-completed-task-${task.id}`}
                  key={task.id}
                  title={task.title}
                  typographyList={typographyList}
                  duration={task.duration}
                  workingTime={task.working_time}
                  onClick={null} // No onClick for completed tasks
                  type="completed"
                />
              );
            })}
          </List>
        ) : (
          <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            No tasks completed yet.
          </Typography>
        )}
      </Box>

      {/* item menu */}
      <Menu
        id="task-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick}>Edit</MenuItem>
        <MenuItem onClick={handleShareClick}>Share</MenuItem>
        <MenuItem onClick={handleCategoryClick}>Change category</MenuItem>
        <MenuItem onClick={handleResetOrder}>Reset Order</MenuItem>
        <MenuItem onClick={handleUnassign}>Unassign</MenuItem>
        <MenuItem disabled>Complete (todo)</MenuItem>
      </Menu>

      <Dialog
        open={showCategoryDialog}
        onClose={() => { setShowCategoryDialog(false); setSelectedTask(null); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Change category</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <CategoryPicker
            value={selectedTaskNode()?.category_id ?? null}
            categories={categories}
            onCategoriesChange={setCategories}
            onChange={handleCategoryChange}
          />
        </DialogContent>
      </Dialog>

      {/* share modal */}
      <Dialog
        id="home-share-dialog"
        open={showShareModal}
        onClose={closeShareModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share task</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            {shareError && <Alert severity="error" sx={{ mb: 2 }}>{shareError}</Alert>}
            {shareMessage && <Alert severity="info" sx={{ mb: 2 }}>{shareMessage}</Alert>}
            {isLoadingFriends ? (
              <LoadingIndicator text="loading friends" />
            ) : shareFriends.length ? (
              <List>
                {shareFriends.map((friend) => (
                  <ListItem
                    key={friend.id}
                    disablePadding
                    onClick={() => toggleSelectedFriend(friend.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Checkbox
                      edge="start"
                      checked={selectedFriendIds.includes(friend.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText primary={friend.username} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No friends yet.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button id="home-share-close" onClick={closeShareModal}>Close</Button>
          <Button
            id="home-share-confirm"
            variant="contained"
            disabled={!selectedFriendIds.length || !!shareMessage}
            onClick={handleShareTask}
          >
            Share
          </Button>
        </DialogActions>
      </Dialog>

      <TaskSelectionDialog
        idPrefix="home-reassign"
        open={showReassignModal}
        onClose={closeReassignModal}
        onConfirm={handleReassignTask}
        tasks={unassignedTasks}
        title="Select task to reassign"
        confirmLabel="Reassign"
        error={reassignError}
        isSubmitting={isReassigning}
        submittingText="reassigning"
      />
      <PointsEarnedPopup
        open={completionPopup.open}
        onClose={handleCompletionPopupClose}
        breakdown={completionPopup.breakdown}
        streakMultiplier={completionPopup.streakMultiplier}
        totalPoints={completionPopup.totalPoints}
      />
    </Container>
  );
};

export default UserHome;
